import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Leaf, Mic, Loader2, Check, Play, Pause, Sparkles } from 'lucide-react';

/* ── Sent2ment palette references ── */
const C = {
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
};

/* The three tarot-style affirmation cards. Order matters — this is the
   left-to-right order the buyer fills in and the recipient receives.
   Palette: brand-aligned cream/champagne with one midnight-navy contrast
   card in the middle ("Shine bright") for visual rhythm. */
export const AFFIRMATION_CARDS = [
  {
    n: 1,
    title: 'Love always',
    Icon: Sun,
    /* Warm cream + champagne gold (matches landing-page preview) */
    bg: 'linear-gradient(160deg, #FBF6E8 0%, #F4ECD3 60%, #E8DAB1 100%)',
    accent: '#9A7E45',
    accentSoft: 'rgba(154, 126, 69, 0.22)',
    halo: 'rgba(251, 246, 232, 0.65)',
    dark: false,
  },
  {
    n: 2,
    title: 'Shine bright',
    Icon: Moon,
    /* Midnight navy + foil gold — the contrast card */
    bg: 'linear-gradient(160deg, #2A3247 0%, #18223A 55%, #0B1F3A 100%)',
    accent: '#C6A96B',
    accentSoft: 'rgba(198, 169, 107, 0.22)',
    halo: 'rgba(11, 31, 58, 0.7)',
    dark: true,
  },
  {
    n: 3,
    title: 'Comfort',
    Icon: Leaf,
    /* Same warm cream + champagne gold as Card 1 — symmetry */
    bg: 'linear-gradient(160deg, #FBF6E8 0%, #F4ECD3 60%, #E8DAB1 100%)',
    accent: '#9A7E45',
    accentSoft: 'rgba(154, 126, 69, 0.22)',
    halo: 'rgba(251, 246, 232, 0.65)',
    dark: false,
  },
];

/* Refined typography colours — deep muted blue (not black), softer body,
   and brand gold tones used consistently across heading + card framing. */
const TITLE_COLOR = '#1E2D45';
const BODY_COLOR = '#3F4E66';
const BRAND_GOLD = '#C6A96B';     /* Sent2ment champagne gold (border) */
const SOFT_GOLD = '#D6C3A3';       /* Softer gold for text/attribution */

/* ── Decorative corner sprigs — purely visual, no functional role ── */
const CornerSprig = ({ accent, position }) => {
  const transforms = {
    'tl': 'translate(8px, 8px)',
    'tr': 'translate(-8px, 8px) scaleX(-1)',
    'bl': 'translate(8px, -8px) scaleY(-1)',
    'br': 'translate(-8px, -8px) scale(-1, -1)',
  };
  const [vAlign, hAlign] = {
    'tl': ['top', 'left'],
    'tr': ['top', 'right'],
    'bl': ['bottom', 'left'],
    'br': ['bottom', 'right'],
  }[position];
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 34 34"
      aria-hidden="true"
      style={{
        position: 'absolute',
        [vAlign]: 0,
        [hAlign]: 0,
        opacity: 0.55,
        transform: transforms[position],
        pointerEvents: 'none',
      }}
    >
      <path
        d="M2 32 C 8 26, 12 20, 14 12 M14 12 C 11 14, 8 14, 5 13 M14 12 C 17 14, 20 14, 23 13 M14 12 C 14 9, 16 6, 19 4"
        stroke={accent}
        strokeWidth="0.9"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="14" cy="12" r="1.4" fill={accent} opacity="0.85" />
    </svg>
  );
};

/* ── A tiny in-card audio playback button used in BOTH edit and view modes.
   Falls back to disabled state if no audio has been recorded yet. ── */
const ListenButton = ({ src, accent, accentSoft, testid }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };

  const disabled = !src;
  /* Slightly deeper accent fill so the button doesn't blend with the
     card background; an inner highlight + outer shadow give it a soft,
     premium lift without breaking the calm tone. */
  const fillBg = disabled
    ? 'rgba(255, 255, 255, 0.55)'
    : `linear-gradient(180deg, ${accent}28 0%, ${accent}3F 100%)`;
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={togglePlay}
        disabled={disabled}
        data-testid={testid}
        aria-label={playing ? 'Pause message' : 'Listen to your message'}
        className="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
        style={{
          background: fillBg,
          border: `1.25px solid ${accent}`,
          borderRadius: '999px',
          color: accent,
          letterSpacing: '0.08em',
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          boxShadow: disabled
            ? 'none'
            : `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 ${accent}33, 0 6px 14px rgba(30, 45, 69, 0.16)`,
        }}
      >
        {playing ? <Pause size={13} strokeWidth={1.6} /> : <Play size={13} strokeWidth={1.6} style={{ marginLeft: '1px' }} />}
        {playing ? 'Pause' : 'Listen to your message'}
      </button>
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="sr-only"
      />
    </div>
  );
};

/* ── Tarot card frame — shared visual shell for edit + view modes.
   When card.dark is true, flips brand-mark, icon-halo and attribution
   colours so they read clearly against the midnight-navy background. ── */
const CardFrame = ({ card, locked, children, testid }) => {
  const { Icon, accent, bg, halo, dark } = card;
  const wordmarkColor = dark ? '#E8DEC5' : TITLE_COLOR;
  const wordmarkGradient = dark
    ? 'linear-gradient(135deg, #8E6F36 0%, #DCC082 50%, #8E6F36 100%)'
    : null;
  const haloBg = dark ? 'rgba(11, 31, 58, 0.45)' : 'rgba(255, 255, 255, 0.6)';
  const haloBorder = dark ? `1px solid ${accent}66` : `1px solid ${accent}40`;
  const attributionColor = dark ? '#DCC082AA' : SOFT_GOLD;

  return (
    <motion.article
      initial={{ y: 14, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 * card.n }}
      whileHover={locked ? {} : { y: -3 }}
      data-testid={testid}
      className="relative flex flex-col px-6 py-7 sm:px-7 sm:py-8 overflow-hidden"
      style={{
        background: bg,
        /* Gold border using the brand's champagne gold so cards feel framed,
           not plain. Slight inner highlight via boxShadow inset. */
        border: `1.5px solid ${BRAND_GOLD}`,
        borderRadius: '18px',
        boxShadow: dark
          ? `inset 0 0 0 1px rgba(220,192,130,0.18), 0 1px 0 ${halo}, 0 18px 40px rgba(11, 31, 58, 0.30)`
          : `inset 0 0 0 1px rgba(255,255,255,0.4), 0 1px 0 ${halo}, 0 14px 36px rgba(30, 45, 69, 0.10)`,
        minHeight: '560px',
      }}
    >
      {/* Decorative corner sprigs */}
      <CornerSprig accent={accent} position="tl" />
      <CornerSprig accent={accent} position="tr" />
      <CornerSprig accent={accent} position="bl" />
      <CornerSprig accent={accent} position="br" />

      {/* Top: Sent2ment brand mark — flips to foil-gold script on dark cards */}
      <div className="flex flex-col items-center mb-2" aria-hidden="true">
        <span
          style={{
            fontFamily: "'Pinyon Script', cursive",
            color: wordmarkColor,
            fontSize: '1.5rem',
            lineHeight: 1,
            ...(wordmarkGradient && {
              background: wordmarkGradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }),
          }}
        >
          Sent2ment
        </span>
      </div>

      {/* Top: small icon halo */}
      <div className="flex flex-col items-center mb-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center"
          style={{
            background: haloBg,
            border: haloBorder,
          }}
        >
          <Icon size={18} strokeWidth={1.4} style={{ color: accent }} />
        </div>
      </div>

      {children}

      {/* Footer: Powered by Spoken Echoes — soft gold attribution
          inside every card so the brand signature is consistent. */}
      <p
        className="text-center italic mt-3"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: attributionColor,
          fontSize: '11px',
          letterSpacing: '0.05em',
          fontWeight: 300,
        }}
        aria-hidden="true"
      >
        Powered by Spoken Echoes
      </p>
    </motion.article>
  );
};

/* ── Edit-mode card — sender writes + records ── */
const EditCard = ({ card, value, onChangeText, audioUrl, onUpload, locked, busy }) => {
  const inputRef = useRef(null);
  const handlePick = () => { if (!locked && !busy) inputRef.current?.click(); };
  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) onUpload(`affirmation_${card.n}`, file);
  };

  // Word-limit (keeps existing 30-word rule from VaultEditPage)
  const tokens = String(value || '').trim().match(/\S+/g) || [];
  const wordsLeft = Math.max(0, 30 - tokens.length);
  const overLimit = tokens.length > 30;

  return (
    <CardFrame card={card} locked={locked} testid={`vault-edit-affirmation-block-${card.n}`}>
      {/* Top: record action — fixed height so all 3 cards align even when
          one card's label says "Voice added — tap to replace" instead of
          "Record softly (optional)". */}
      <div
        className="flex flex-col items-center justify-center -mt-4 mb-5"
        style={{ minHeight: '2.6em' }}
      >
        <button
          type="button"
          onClick={handlePick}
          disabled={locked || busy}
          data-testid={`vault-edit-audio-pick-affirmation_${card.n}`}
          className="inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-50 text-center"
          style={{
            background: 'transparent',
            color: card.accent,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '12px',
            letterSpacing: '0.04em',
            lineHeight: 1.3,
          }}
        >
          {busy ? (
            <><Loader2 size={12} className="animate-spin" /> Uploading…</>
          ) : audioUrl ? (
            <><Check size={12} /> Voice added — tap to replace</>
          ) : (
            <><Mic size={12} strokeWidth={1.6} /> Record softly (optional)</>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.oga,.opus,.webm,.amr,.3gp,.3gpp,.flac"
          onChange={handleChange}
          className="hidden"
          data-testid={`vault-edit-audio-input-affirmation_${card.n}`}
        />
      </div>

      {/* Centre: title — fixed minHeight reserves space for 2 lines so
          "Comfort" (1 word) aligns with "Love always" / "Shine bright" (2 words). */}
      <h3
        className="text-center mb-1.5 flex items-center justify-center"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: card.dark ? '#F2E7C8' : TITLE_COLOR,
          fontSize: '2rem',
          fontWeight: 500,
          letterSpacing: '0.005em',
          lineHeight: 1.05,
          minHeight: '4.4rem',
        }}
        data-testid={`vault-edit-affirmation-title-${card.n}`}
      >
        {card.title}
      </h3>
      <div className="flex justify-center mb-5" aria-hidden="true">
        <Sparkles size={11} strokeWidth={1.5} style={{ color: card.accent, opacity: 0.7 }} />
      </div>

      {/* Middle: textarea */}
      <div className="flex-1 mb-5 flex flex-col">
        <textarea
          value={value || ''}
          onChange={(e) => {
            const next = e.target.value;
            const t = next.trim().match(/\S+/g) || [];
            if (t.length <= 30) {
              onChangeText(`affirmation_message_${card.n}`, next);
            } else {
              onChangeText(`affirmation_message_${card.n}`, t.slice(0, 30).join(' '));
            }
          }}
          placeholder="Write your affirmation here…"
          rows={4}
          maxLength={5000}
          disabled={locked}
          data-testid={`vault-edit-affirmation-textarea-${card.n}`}
          className="w-full px-4 py-3 outline-none transition-colors duration-300 resize-none disabled:opacity-60 flex-1"
          style={{
            background: card.dark ? 'rgba(11, 31, 58, 0.45)' : 'rgba(255, 255, 255, 0.78)',
            color: card.dark ? '#F2E7C8' : BODY_COLOR,
            border: `1px solid ${overLimit ? '#B33A3A' : `${card.accent}55`}`,
            borderRadius: '10px',
            fontFamily: 'Georgia, serif',
            fontSize: '14px',
            lineHeight: '1.7',
            backdropFilter: 'blur(2px)',
            minHeight: '140px',
          }}
        />
        <p
          className="text-[10px] italic mt-2 text-right"
          style={{ color: overLimit ? '#B33A3A' : `${card.accent}AA`, fontFamily: 'Georgia, serif' }}
        >
          {wordsLeft} words left · 30 max
        </p>
      </div>

      {/* Bottom: listen */}
      <ListenButton
        src={audioUrl}
        accent={card.accent}
        accentSoft={card.accentSoft}
        testid={`vault-edit-affirmation-listen-${card.n}`}
      />
    </CardFrame>
  );
};

/* ── View-mode card — recipient reads + listens.
   Premium "turn over" interaction (mirrors landing-page preview): each card
   stays closed showing the title + sparkle + gentle "Tap to reveal" hint
   on the front, and the full message + listen button live on the back.
   This gives the recipient a small, intentional moment of revealing each
   message rather than reading them all at once.
   The CardFrame visual shell is preserved on BOTH faces so the brand
   wordmark and gold border feel continuous through the flip. ── */
const ViewCardFace = ({ card, children, testid }) => (
  <CardFrame card={card} locked testid={testid}>{children}</CardFrame>
);

const ViewCard = ({ card, text, audioUrl }) => {
  const [flipped, setFlipped] = React.useState(false);
  const toggle = () => setFlipped((f) => !f);
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div
      style={{ perspective: '1400px' }}
      data-testid={`vault-view-affirmation-${card.n}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={onKey}
        aria-label={flipped ? 'Hide message' : 'Reveal message'}
        aria-pressed={flipped}
        data-testid={`vault-view-affirmation-flip-${card.n}`}
        className="cursor-pointer focus:outline-none"
        style={{ outline: 'none' }}
      >
        <motion.div
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.95, ease: [0.45, 0.05, 0.35, 1] }}
          style={{
            position: 'relative',
            transformStyle: 'preserve-3d',
            minHeight: '560px',
          }}
        >
          {/* ── Front face — title + sparkle + subtle reveal prompt ── */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              position: 'relative',
            }}
          >
            <ViewCardFace card={card} testid={`vault-view-affirmation-front-${card.n}`}>
              <h3
                className="text-center mb-1.5 flex items-center justify-center"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: card.dark ? '#F2E7C8' : TITLE_COLOR,
                  fontSize: '2rem',
                  fontWeight: 500,
                  letterSpacing: '0.005em',
                  lineHeight: 1.05,
                  minHeight: '4.4rem',
                }}
              >
                {card.title}
              </h3>
              <div className="flex justify-center mb-5" aria-hidden="true">
                <Sparkles size={11} strokeWidth={1.5} style={{ color: card.accent, opacity: 0.7 }} />
              </div>
              <div className="flex-1 flex flex-col items-center justify-center mb-5 px-4">
                <p
                  className="text-center italic"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: card.dark ? '#DCC082CC' : `${BODY_COLOR}AA`,
                    fontSize: '14px',
                    lineHeight: 1.7,
                    letterSpacing: '0.02em',
                  }}
                >
                  A message kept just for you.
                </p>
              </div>
              <p
                className="text-center"
                style={{
                  fontFamily: 'Georgia, serif',
                  color: card.dark ? '#DCC082' : card.accent,
                  fontSize: '10px',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                }}
                data-testid={`vault-view-affirmation-front-hint-${card.n}`}
              >
                Tap to reveal
              </p>
            </ViewCardFace>
          </div>

          {/* ── Back face — the actual message + listen button ── */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              position: 'absolute',
              inset: 0,
              transform: 'rotateY(180deg)',
            }}
          >
            <ViewCardFace card={card} testid={`vault-view-affirmation-back-${card.n}`}>
              <h3
                className="text-center mb-1.5 flex items-center justify-center"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: card.dark ? '#F2E7C8' : TITLE_COLOR,
                  fontSize: '2rem',
                  fontWeight: 500,
                  letterSpacing: '0.005em',
                  lineHeight: 1.05,
                  minHeight: '4.4rem',
                }}
              >
                {card.title}
              </h3>
              <div className="flex justify-center mb-5" aria-hidden="true">
                <Sparkles size={11} strokeWidth={1.5} style={{ color: card.accent, opacity: 0.7 }} />
              </div>
              <div className="flex-1 mb-5 flex flex-col">
                <div
                  className="flex-1 px-4 py-3 flex items-center justify-center"
                  style={{
                    background: card.dark ? 'rgba(11, 31, 58, 0.45)' : 'rgba(255, 255, 255, 0.78)',
                    border: `1px solid ${card.accent}55`,
                    borderRadius: '10px',
                    backdropFilter: 'blur(2px)',
                    minHeight: '140px',
                  }}
                >
                  {text ? (
                    <p
                      className="text-center italic"
                      style={{
                        color: card.dark ? '#F2E7C8' : TITLE_COLOR,
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: '17px',
                        lineHeight: '1.7',
                        whiteSpace: 'pre-wrap',
                      }}
                      data-testid={`vault-view-affirmation-text-${card.n}`}
                    >
                      “{text}”
                    </p>
                  ) : (
                    <p
                      className="text-center italic"
                      style={{
                        color: card.dark ? '#DCC082AA' : `${BODY_COLOR}99`,
                        fontFamily: 'Georgia, serif',
                        fontSize: '13px',
                      }}
                    >
                      {audioUrl ? 'A voice note for you below.' : 'Kept gently quiet by your sender.'}
                    </p>
                  )}
                </div>
                <p className="text-[10px] italic mt-2 text-right" aria-hidden="true">&nbsp;</p>
              </div>
              {/* Stop click bubbling so tapping the listen button doesn't flip the card. */}
              <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <ListenButton
                  src={audioUrl}
                  accent={card.accent}
                  accentSoft={card.accentSoft}
                  testid={`vault-view-affirmation-listen-${card.n}`}
                />
              </div>
            </ViewCardFace>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

/* ── Shared heading block (centered, premium, used by both views).
   "Powered by Spoken Echoes" attribution lives inside each card now,
   so the heading stays clean and uncluttered. ── */
export const AffirmationsHeading = () => (
  <div
    className="text-center mb-10 sm:mb-12"
    data-testid="affirmations-heading-block"
  >
    <h2
      className="font-light tracking-tight"
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        color: TITLE_COLOR,
        fontSize: 'clamp(2rem, 4vw, 2.75rem)',
        lineHeight: 1.1,
        marginBottom: '0.65em',
      }}
      data-testid="affirmations-heading-title"
    >
      Three Affirmations
    </h2>
    <p
      className="italic mx-auto"
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        color: `${BODY_COLOR}B3`,
        fontSize: 'clamp(0.95rem, 1.4vw, 1.1rem)',
        lineHeight: 1.6,
        maxWidth: '32em',
      }}
      data-testid="affirmations-heading-subtitle"
    >
      Words they can return to, whenever they feel ready.
    </p>
  </div>
);

/* ── Sender (edit) layout: 3 cards side-by-side, stacked on mobile ── */
export const AffirmationCardsEdit = ({
  textDraft,
  onChangeText,
  content,
  onUpload,
  locked,
  audioBusy,
}) => (
  <div
    className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6"
    data-testid="vault-edit-affirmation-cards"
  >
    {AFFIRMATION_CARDS.map((card) => {
      const slot = `affirmation_${card.n}`;
      const audioCol = `affirmation_${card.n}_audio_url`;
      return (
        <EditCard
          key={card.n}
          card={card}
          value={textDraft[`affirmation_message_${card.n}`]}
          onChangeText={onChangeText}
          audioUrl={content[audioCol]}
          onUpload={onUpload}
          locked={locked}
          busy={!!audioBusy[slot]}
        />
      );
    })}
  </div>
);

/* ── Recipient (view) layout: 3 cards side-by-side, stacked on mobile.
   Always renders all 3 cards so the recipient sees the full gift
   structure. Empty cards show a calm placeholder instead of vanishing. ── */
export const AffirmationCardsView = ({ vault }) => {
  const cards = AFFIRMATION_CARDS.map((c) => ({
    card: c,
    text: vault[`affirmation_message_${c.n}`],
    audioUrl: vault[`affirmation_${c.n}_audio_url`],
  }));
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6"
      data-testid="vault-view-affirmation-cards"
    >
      {cards.map(({ card, text, audioUrl }) => (
        <ViewCard key={card.n} card={card} text={text} audioUrl={audioUrl} />
      ))}
    </div>
  );
};
